import './dashboard-dark.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartLineUp,
  Wallet,
  Users,
  Clock,
  UserCircle,
  CurrencyDollar,
  Fire,
  SlidersHorizontal,
  Check,
  Bell,
} from '@phosphor-icons/react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useCurrency } from '@/hooks/useCurrency';
import ActivityPulse from '@/components/ActivityPulse';
import FounderDashboardSideRail from '@/components/FounderDashboardSideRail';
import { getDashboardGreeting } from '@/utils/dashboardGreetings';

const DEMO_CHART_DATA = [
  { month: 'Aug', mrr: 6800, revenue: 22000, burn: 36000, cash: 47000, runway: 4.4, customers: 82, headcount: 2 },
  { month: 'Sep', mrr: 7200, revenue: 24000, burn: 37000, cash: 50000, runway: 4.7, customers: 96, headcount: 2 },
  { month: 'Oct', mrr: 7700, revenue: 25500, burn: 38000, cash: 52000, runway: 4.9, customers: 112, headcount: 2 },
  { month: 'Nov', mrr: 8100, revenue: 27000, burn: 39000, cash: 54500, runway: 5.1, customers: 126, headcount: 3 },
  { month: 'Dec', mrr: 8500, revenue: 28500, burn: 40500, cash: 57000, runway: 5.3, customers: 142, headcount: 3 },
  { month: 'Jan', mrr: 9100, revenue: 30000, burn: 41000, cash: 59000, runway: 5.5, customers: 156, headcount: 3 },
  { month: 'Feb', mrr: 9600, revenue: 31000, burn: 42000, cash: 61000, runway: 5.8, customers: 170, headcount: 3 },
  { month: 'Mar', mrr: 10100, revenue: 35000, burn: 44000, cash: 68000, runway: 6.1, customers: 210, headcount: 4 },
  { month: 'Apr', mrr: 10900, revenue: 38000, burn: 46000, cash: 72000, runway: 6.8, customers: 255, headcount: 5 },
  { month: 'May', mrr: 12500, revenue: 43000, burn: 47000, cash: 79000, runway: 7.2, customers: 305, headcount: 6 },
  { month: 'Jun', mrr: 11600, revenue: 39000, burn: 45000, cash: 82000, runway: 7.6, customers: 355, headcount: 6 },
  { month: 'Jul', mrr: 13000, revenue: 46000, burn: 48000, cash: 85000, runway: 8, customers: 420, headcount: 6 },
];

const DEMO_METRICS = {
  mrr: 13000,
  cash: 85000,
  runway: 8,
  headcount: 6,
  customers: 420,
  revenue: 46000,
  netBurn: 2000,
};

const DEFAULT_METRIC_KEYS = ['mrr', 'cash', 'activeUsers'];
const MIN_METRICS = 3;
const MAX_METRICS = 5;

const normalizeMetricKeys = (stored) => {
  if (!Array.isArray(stored)) return DEFAULT_METRIC_KEYS;
  const available = ['mrr', 'cash', 'activeUsers', 'runway', 'team', 'revenue', 'netBurn'];
  // Restore the customer card for browsers that received the temporary Team Size default.
  if (stored.length === 3 && ['mrr', 'cash', 'team'].every((key) => stored.includes(key))) return DEFAULT_METRIC_KEYS;
  const keys = [...new Set(stored)]
    .filter((key) => available.includes(key)).slice(0, MAX_METRICS);
  for (const key of DEFAULT_METRIC_KEYS) {
    if (keys.length >= MIN_METRICS) break;
    if (!keys.includes(key)) keys.push(key);
  }
  return keys;
};

const CHARTS = [
  { key: 'mrr', title: 'MRR Trend', subtitle: 'Monthly recurring revenue', series: [{ dataKey: 'mrr', label: 'MRR', money: true }], type: 'area' },
  { key: 'revenue', title: 'Revenue vs Burn', subtitle: 'Income compared with operating burn', series: [{ dataKey: 'revenue', label: 'Revenue', money: true, render: 'bar' }, { dataKey: 'burn', label: 'Burn', money: true, render: 'line' }], type: 'composed' },
  { key: 'cash', title: 'Cash in Bank', subtitle: 'Total liquid capital available', series: [{ dataKey: 'cash', label: 'Cash', money: true }], type: 'area' },
  { key: 'runway', title: 'Runway', subtitle: 'Calculated months of runway remaining', series: [{ dataKey: 'runway', label: 'Runway' }], type: 'line' },
  { key: 'customers', title: 'Customers', subtitle: 'Cumulative customer relationships', series: [{ dataKey: 'customers', label: 'Customers' }], type: 'bar' },
  { key: 'headcount', title: 'Headcount', subtitle: 'Full-time team size', series: [{ dataKey: 'headcount', label: 'Headcount' }], type: 'line', step: true },
];

const DEFAULT_GRAPH_KEYS = ['mrr', 'revenue'];

const SERIES_COLORS = ['var(--dashboard-series-teal, #0D9488)', 'var(--dashboard-series-amber, #F59E0B)', 'var(--dashboard-series-violet, #6366F1)'];
const DEMO_SERIES_COLORS = ['#94A3B8', '#64748B', '#CBD5E1'];

const DemoTag = ({ prominent = false }) => (
  <span className={`${prominent ? 'px-2 py-1 text-[9px]' : 'px-1.5 py-0.5 text-[8px]'} rounded-md border border-slate-300 bg-slate-100 font-extrabold uppercase tracking-[0.1em] text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400`}>
    Demo Data
  </span>
);

const TrendHint = ({ value, tone = 'neutral' }) => {
  if (!value) return null;
  const tones = {
    positive: 'text-emerald-600 dark:text-emerald-300',
    negative: 'text-rose-600 dark:text-rose-300',
    warning: 'text-amber-700 dark:text-amber-300',
    neutral: 'text-text-muted',
  };
  const dots = {
    positive: 'bg-emerald-500',
    negative: 'bg-rose-500',
    warning: 'bg-amber-500',
    neutral: 'bg-slate-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[9px] font-semibold ${tones[tone] || tones.neutral}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dots[tone] || dots.neutral}`} />
      {value}
    </span>
  );
};

const MetricCard = ({ label, value, demo, footer, icon: Icon, trend, trendTone = 'neutral', onDemoClick }) => {
  const cardStyle = demo
    ? 'fb-demo-metric group cursor-pointer border-slate-300 bg-slate-100/70 opacity-80 ring-1 ring-slate-200/70 transition hover:opacity-100 dark:border-slate-700 dark:bg-slate-900/55 dark:ring-slate-800'
    : 'border-border bg-surface';
  return (
    <div
      className={`fb-metric-card relative min-w-0 overflow-hidden rounded-lg border px-3.5 py-3.5 ${cardStyle}`}
      onClick={demo ? onDemoClick : undefined}
      role={demo ? 'button' : undefined}
      tabIndex={demo ? 0 : undefined}
      onKeyDown={demo ? (event) => { if (event.key === 'Enter' || event.key === ' ') onDemoClick?.(); } : undefined}
    >
      {demo && <div className="absolute inset-x-0 top-0 h-0.5 bg-slate-400 dark:bg-slate-600" />}
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            {Icon && <Icon size={16} weight="bold" className={demo ? 'text-slate-500 dark:text-slate-400' : 'text-[#0D9488] dark:text-teal-300'} />}
            <span className="truncate text-[11px] font-extrabold uppercase tracking-[0.07em] text-text-muted sm:text-[11px]">{label}</span>
          </div>
        </div>
        {demo && <DemoTag />}
      </div>
      <div className={`truncate font-heading text-[19px] font-bold tracking-[-0.025em] sm:text-[21px] ${demo ? 'text-slate-500 dark:text-slate-400' : 'text-text-primary'}`}>{value}</div>
      <div className="mt-2 flex min-h-[18px] items-center gap-2">
        <TrendHint value={trend} tone={demo ? 'neutral' : trendTone} />
        {footer && <span className="truncate text-[10px] text-text-muted sm:text-[11px]">{footer}</span>}
      </div>
      {demo && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/58 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
          <span className="border border-white/30 bg-black/25 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur-sm">Add your data</span>
        </div>
      )}
    </div>
  );
};

const DashboardChart = ({ chart, data, demo, formatCompact, chartHeight = 210, animationDelay = 0, compact = false, onDemoClick }) => {
  const tooltipStyle = {
    backgroundColor: 'var(--card)',
    border: '1px solid color-mix(in srgb, var(--border) 78%, var(--foreground) 22%)',
    borderRadius: 10,
    fontSize: 11,
    color: 'var(--foreground)',
    opacity: 1,
    boxShadow: '0 18px 44px rgba(15,23,42,.24)',
  };

  const primary = chart.series[0];
  const latest = data?.[data.length - 1]?.[primary.dataKey];
  const previous = data?.[data.length - 2]?.[primary.dataKey];
  const delta = Number(previous) ? ((Number(latest) - Number(previous)) / Math.abs(Number(previous))) * 100 : null;
  const formatValue = (value, series = primary) => {
    if (series.money) return formatCompact(value);
    if (series.percent) return `${Number(value ?? 0).toFixed(1)}%`;
    if (chart.key === 'runway') return `${Number(value ?? 0).toFixed(1)} mo`;
    return Number(value ?? 0).toLocaleString();
  };
  const yTick = (value) => primary.money ? formatCompact(value) : primary.percent ? `${Number(value).toFixed(0)}%` : value;

  const commonAxis = (
    <>
      <CartesianGrid strokeDasharray="2 5" stroke="var(--border)" vertical={false} />
      <XAxis dataKey="month" height={compact ? 18 : 30} tick={{ fontSize: compact ? 8 : 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
      <YAxis hide={compact} tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={44} tickFormatter={yTick} />
      <Tooltip
        contentStyle={tooltipStyle}
        cursor={{ fill: 'color-mix(in srgb, var(--primary) 7%, transparent)' }}
        wrapperStyle={{ zIndex: 40, pointerEvents: 'none', outline: 'none' }}
        labelStyle={{ color: 'var(--foreground)', fontWeight: 700, marginBottom: 4 }}
        itemStyle={{ color: 'var(--foreground)', paddingTop: 2 }}
        formatter={(v, name) => {
          const series = chart.series.find((item) => item.dataKey === name) || primary;
          return [formatValue(v, series), series.label];
        }}
      />
    </>
  );

  return (
    <div
      className={`fb-chart-card relative flex h-auto min-h-[230px] min-w-0 flex-col overflow-hidden rounded-lg border p-2.5 sm:min-h-[250px] sm:p-3 xl:h-full xl:min-h-0 ${demo ? 'fb-demo-chart group cursor-pointer border-slate-300 bg-slate-100/60 opacity-80 transition hover:opacity-100 dark:border-slate-700 dark:bg-slate-900/45' : 'border-border bg-surface'}`}
      data-testid={`dashboard-chart-${chart.key}`}
      style={{ animationDelay: `${animationDelay}ms` }}
      onClick={demo ? onDemoClick : undefined}
      role={demo ? 'button' : undefined}
      tabIndex={demo ? 0 : undefined}
      onKeyDown={demo ? (event) => { if (event.key === 'Enter' || event.key === ' ') onDemoClick?.(); } : undefined}
    >
      {demo && <div className="absolute inset-x-0 top-0 h-0.5 bg-slate-400/80 dark:bg-slate-600/80" />}
      <div className="fb-chart-heading flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[11px] sm:text-[12px] font-bold text-text-primary truncate">{chart.title}</div>
          <div className="text-[8px] sm:text-[9px] text-text-muted truncate mt-0.5">{chart.subtitle}</div>
        </div>
        {demo && <DemoTag />}
      </div>

      <div className="fb-chart-summary flex items-end justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <div className="text-[17px] sm:text-[20px] font-heading font-bold tracking-[-0.025em] text-text-primary truncate">{formatValue(latest)}</div>
          <div className="fb-chart-latest-label text-[9px] text-text-muted mt-0.5">Latest value</div>
        </div>
        {delta !== null && Number.isFinite(delta) && (
          <div className={`rounded-md px-2 py-1 text-[10px] font-bold ${delta >= 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300' : 'bg-rose-500/10 text-rose-600 dark:text-rose-300'}`}>
            {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
          </div>
        )}
      </div>

      <div style={{ height: chartHeight }} className="fb-chart-viewport min-h-0 flex-1 transition-[height] duration-500 ease-[cubic-bezier(.16,1,.3,1)]">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'bar' ? (
            <BarChart data={data} margin={{ top: compact ? 6 : 16, right: 8, left: compact ? 0 : -16, bottom: 0 }}>
              {commonAxis}
              {chart.series.map((series, index) => (
                <Bar key={series.dataKey} dataKey={series.dataKey} fill={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[index % (demo ? DEMO_SERIES_COLORS.length : SERIES_COLORS.length)]} radius={[5, 5, 0, 0]} maxBarSize={28} isAnimationActive animationBegin={120 + (index * 90)} animationDuration={1050} animationEasing="ease-out" />
              ))}
            </BarChart>
          ) : chart.type === 'area' ? (
            <AreaChart data={data} margin={{ top: compact ? 6 : 16, right: 8, left: compact ? 0 : -16, bottom: 0 }}>
              <defs>
                <linearGradient id={`fill-${chart.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[0]} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[0]} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              {commonAxis}
              <Area type="monotone" dataKey={primary.dataKey} stroke={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[0]} fill={`url(#fill-${chart.key})`} strokeWidth={2.7} dot={false} activeDot={{ r: 4.5 }} isAnimationActive animationBegin={100} animationDuration={1250} animationEasing="ease-out" />
            </AreaChart>
          ) : chart.type === 'composed' ? (
            <ComposedChart data={data} margin={{ top: compact ? 6 : 16, right: 8, left: compact ? 0 : -16, bottom: 0 }}>
              {commonAxis}
              {chart.series.map((series, index) => series.render === 'bar' ? (
                <Bar key={series.dataKey} dataKey={series.dataKey} fill={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[index % (demo ? DEMO_SERIES_COLORS.length : SERIES_COLORS.length)]} radius={[5, 5, 0, 0]} maxBarSize={25} isAnimationActive animationBegin={140 + (index * 100)} animationDuration={1100} animationEasing="ease-out" />
              ) : (
                <Line key={series.dataKey} type="monotone" dataKey={series.dataKey} stroke={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[index % (demo ? DEMO_SERIES_COLORS.length : SERIES_COLORS.length)]} strokeWidth={2.7} dot={false} activeDot={{ r: 4.5 }} isAnimationActive animationBegin={180 + (index * 110)} animationDuration={1250} animationEasing="ease-out" />
              ))}
            </ComposedChart>
          ) : (
            <LineChart data={data} margin={{ top: compact ? 6 : 16, right: 8, left: compact ? 0 : -16, bottom: 0 }}>
              {commonAxis}
              {chart.series.map((series, index) => (
                <Line key={series.dataKey} type={chart.step ? 'stepAfter' : 'monotone'} dataKey={series.dataKey} stroke={(demo ? DEMO_SERIES_COLORS : SERIES_COLORS)[index % (demo ? DEMO_SERIES_COLORS.length : SERIES_COLORS.length)]} strokeWidth={2.65} dot={false} activeDot={{ r: 4.5 }} isAnimationActive animationBegin={120 + (index * 100)} animationDuration={1200} animationEasing="ease-out" />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {demo && <div className="fb-chart-demo-note mt-2 border border-slate-300 bg-slate-200/55 px-2.5 py-1.5 text-[8px] font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">DEMO series — monthly updates will replace this with your actual trend.</div>}
      {demo && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/58 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
          <span className="border border-white/30 bg-black/25 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-white backdrop-blur-sm">Add your data</span>
        </div>
      )}
    </div>
  );
};

const StartupDashboardNew = ({ data, user }) => {
  const navigate = useNavigate();
  const addMonthlyData = () => navigate('/startup/update');
  const { formatCompact } = useCurrency();
  const latestUpdate = data?.latest_update || null;
  const startup = data?.startup || null;
  const realChartData = data?.chart_data || [];
  const useDemoCharts = realChartData.length < 2;
  const baseChartData = useDemoCharts ? DEMO_CHART_DATA : realChartData;
  const mom = data?.mom || {};
  const [duration, setDuration] = useState('6M');
  const graphPreferenceKey = useMemo(() => {
    const identity = user?._id || user?.id || user?.email || user?.username || 'default';
    return `founderboard_dashboard_graphs_v2:${identity}`;
  }, [user]);
  const [selectedGraphs, setSelectedGraphs] = useState(() => {
    try {
      const identity = user?._id || user?.id || user?.email || user?.username || 'default';
      const stored = localStorage.getItem(`founderboard_dashboard_graphs_v2:${identity}`);
      if (!stored) return DEFAULT_GRAPH_KEYS;
      const parsed = JSON.parse(stored);
      const valid = Array.isArray(parsed) ? parsed.filter((key) => CHARTS.some((chart) => chart.key === key)) : [];
      return valid.length ? valid : DEFAULT_GRAPH_KEYS;
    } catch (error) {
      return DEFAULT_GRAPH_KEYS;
    }
  });
  const metricPreferenceKey = useMemo(() => {
    const identity = user?._id || user?.id || user?.email || user?.username || 'default';
    return `founderboard_dashboard_metrics_v2:${identity}`;
  }, [user]);
  const [selectedMetricKeys, setSelectedMetricKeys] = useState(() => {
    try {
      const identity = user?._id || user?.id || user?.email || user?.username || 'default';
      const stored = localStorage.getItem(`founderboard_dashboard_metrics_v2:${identity}`);
      if (!stored) return DEFAULT_METRIC_KEYS;
      const parsed = JSON.parse(stored);
      return normalizeMetricKeys(parsed);
    } catch {
      return DEFAULT_METRIC_KEYS;
    }
  });
  const [metricPickerOpen, setMetricPickerOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dashboardRoot = useRef(null);

  useEffect(() => {
    const root = dashboardRoot.current;
    if (!root) return undefined;
    const host = root.closest('main');
    const updateSize = () => {
      const offset = host ? root.getBoundingClientRect().top - host.getBoundingClientRect().top + host.scrollTop : root.getBoundingClientRect().top + window.scrollY;
      const available = Math.floor((host?.clientHeight || window.innerHeight) - offset - 2);
      root.style.setProperty('--fb-dashboard-height', `${Math.max(0, available)}px`);
      root.dataset.fit = String(window.innerWidth >= 1024 && available >= 600 && !metricPickerOpen);
    };
    updateSize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateSize) : null;
    if (host) observer?.observe(host);
    const aiHost = host?.querySelector('.fb-ai-sticky-shell');
    if (aiHost) observer?.observe(aiHost);
    window.addEventListener('resize', updateSize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', updateSize); };
  }, [metricPickerOpen]);
  const loadedMetricPreferenceKey = useRef(metricPreferenceKey);

  const loadedGraphPreferenceKey = useRef(graphPreferenceKey);
  const greetingChoice = useMemo(() => getDashboardGreeting(), []);
  const [typedGreeting, setTypedGreeting] = useState('');
  const [greetingTyping, setGreetingTyping] = useState(true);
  const [greetingEmojiVisible, setGreetingEmojiVisible] = useState(false);

  useEffect(() => {
    if (loadedGraphPreferenceKey.current === graphPreferenceKey) return;
    loadedGraphPreferenceKey.current = graphPreferenceKey;
    try {
      const stored = localStorage.getItem(graphPreferenceKey);
      const parsed = stored ? JSON.parse(stored) : null;
      const valid = Array.isArray(parsed) ? parsed.filter((key) => CHARTS.some((chart) => chart.key === key)) : [];
      setSelectedGraphs(valid.length ? valid : DEFAULT_GRAPH_KEYS);
    } catch (error) {
      setSelectedGraphs(DEFAULT_GRAPH_KEYS);
    }
  }, [graphPreferenceKey]);

  useEffect(() => {
    try {
      localStorage.setItem(graphPreferenceKey, JSON.stringify(selectedGraphs));
    } catch (error) {
      // Dashboard preferences are non-critical if storage is unavailable.
    }
  }, [graphPreferenceKey, selectedGraphs]);

  useEffect(() => {
    if (loadedMetricPreferenceKey.current === metricPreferenceKey) return;
    loadedMetricPreferenceKey.current = metricPreferenceKey;
    try {
      const stored = localStorage.getItem(metricPreferenceKey);
      const parsed = stored ? JSON.parse(stored) : null;
      setSelectedMetricKeys(normalizeMetricKeys(parsed));
    } catch {
      setSelectedMetricKeys(DEFAULT_METRIC_KEYS);
    }
  }, [metricPreferenceKey]);

  useEffect(() => {
    try {
      localStorage.setItem(metricPreferenceKey, JSON.stringify(selectedMetricKeys));
    } catch {
      // Dashboard preferences are non-critical if storage is unavailable.
    }
  }, [metricPreferenceKey, selectedMetricKeys]);

  useEffect(() => {
    const firstNameForGreeting = user?.full_name?.split(' ')?.[0] || 'Founder';
    const fullGreeting = `${greetingChoice.text}, ${firstNameForGreeting}.`;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reduceMotion) {
      setTypedGreeting(fullGreeting);
      setGreetingTyping(false);
      setGreetingEmojiVisible(true);
      const fadeTimer = window.setTimeout(() => setGreetingEmojiVisible(false), 1400);
      return () => window.clearTimeout(fadeTimer);
    }

    setTypedGreeting('');
    setGreetingTyping(true);
    setGreetingEmojiVisible(false);

    let index = 0;
    let typeTimer;
    let holdTimer;

    const typeNextCharacter = () => {
      index += 1;
      setTypedGreeting(fullGreeting.slice(0, index));

      if (index < fullGreeting.length) {
        typeTimer = window.setTimeout(typeNextCharacter, 42 + Math.floor(Math.random() * 28));
        return;
      }

      setGreetingTyping(false);
      setGreetingEmojiVisible(true);
      holdTimer = window.setTimeout(() => setGreetingEmojiVisible(false), 1500);
    };

    typeTimer = window.setTimeout(typeNextCharacter, 260);

    return () => {
      window.clearTimeout(typeTimer);
      window.clearTimeout(holdTimer);
    };
  }, [greetingChoice, user?.full_name]);

  const metric = (field, fallback) => {
    const value = latestUpdate?.[field];
    const missing = value === undefined || value === null || value === '' || Number.isNaN(Number(value));
    return { value: missing ? fallback : value, demo: missing };
  };

  const mrr = metric('mrr', DEMO_METRICS.mrr);
  const cash = metric('cash_in_bank', DEMO_METRICS.cash);
  const runway = metric('runway_months', DEMO_METRICS.runway);
  const team = metric('headcount', DEMO_METRICS.headcount);
  const customers = metric('total_customers', DEMO_METRICS.customers);

  const latestRealChartRow = realChartData[realChartData.length - 1] || null;
  const previousRealChartRow = realChartData[realChartData.length - 2] || null;
  const chartMetric = (field, fallback) => {
    const raw = latestRealChartRow?.[field];
    const missing = raw === undefined || raw === null || raw === '' || Number.isNaN(Number(raw));
    return { value: missing ? fallback : Number(raw), demo: missing };
  };

  const revenue = chartMetric('revenue', DEMO_METRICS.revenue);
  const latestBurn = latestRealChartRow?.burn;
  const latestRevenue = latestRealChartRow?.revenue;
  const hasNetBurn = latestBurn !== undefined && latestBurn !== null && latestRevenue !== undefined && latestRevenue !== null
    && Number.isFinite(Number(latestBurn)) && Number.isFinite(Number(latestRevenue));
  const netBurn = {
    value: hasNetBurn ? Number(latestBurn) - Number(latestRevenue) : DEMO_METRICS.netBurn,
    demo: !hasNetBurn,
  };

  const toggleMetric = (key) => {
    setSelectedMetricKeys((current) => {
      if (current.includes(key)) {
        if (current.length <= MIN_METRICS) return current;
        return current.filter((item) => item !== key);
      }
      if (current.length >= MAX_METRICS) return current;
      return [...current, key];
    });
  };

  const metricDefinitions = [
    {
      key: 'mrr',
      label: 'MRR',
      icon: ChartLineUp,
      value: formatCompact(mrr.value),
      demo: mrr.demo,
      trend: mrr.demo ? '+18.2%' : `${mom.mrr_pct > 0 ? '+' : ''}${mom.mrr_pct ?? 0}%`,
      trendTone: (mrr.demo ? 18.2 : Number(mom.mrr_pct ?? 0)) >= 0 ? 'positive' : 'negative',
      footer: mrr.demo ? 'Target ₹15.0K' : 'vs last month',
    },
    {
      key: 'cash',
      label: 'Cash in Bank',
      icon: Wallet,
      value: formatCompact(cash.value),
      demo: cash.demo,
      trend: cash.demo ? '+12.1%' : `${mom.cash_pct > 0 ? '+' : ''}${mom.cash_pct ?? 0}%`,
      trendTone: (cash.demo ? 12.1 : Number(mom.cash_pct ?? 0)) >= 0 ? 'positive' : 'negative',
      footer: cash.demo ? 'Burn ₹4K/mo' : 'vs last month',
    },
    {
      key: 'activeUsers',
      label: 'Total Customers',
      icon: UserCircle,
      value: Number(customers.value).toLocaleString(),
      demo: customers.demo,
      trend: customers.demo ? '+7.4%' : Number.isFinite(mom.customers_pct) ? `${mom.customers_pct > 0 ? '+' : ''}${mom.customers_pct}%` : 'Reported',
      trendTone: customers.demo || Number(mom.customers_pct) > 0 ? 'positive' : Number(mom.customers_pct) < 0 ? 'negative' : 'neutral',
      footer: customers.demo || Number.isFinite(mom.customers_pct) ? 'vs last month' : 'latest MIS update',
    },
    {
      key: 'runway',
      label: 'Runway',
      icon: Clock,
      value: `${Number(runway.value).toFixed(Number(runway.value) % 1 ? 1 : 0)} Months`,
      demo: runway.demo,
      trend: Number(runway.value) >= 6 ? 'Stable' : 'Watch',
      trendTone: Number(runway.value) >= 6 ? 'positive' : 'warning',
      footer: Number(runway.value) >= 6 ? 'safe threshold' : 'below 6 months',
    },
    {
      key: 'team',
      label: 'Team Size',
      icon: Users,
      value: `${Number(team.value)} ${Number(team.value) === 1 ? 'Person' : 'People'}`,
      demo: team.demo,
      trend: team.demo ? 'Stable' : mom.headcount_delta ? `${mom.headcount_delta > 0 ? '+' : ''}${mom.headcount_delta}` : 'Stable',
      trendTone: mom.headcount_delta < 0 ? 'negative' : 'neutral',
      footer: team.demo ? '2 roles open' : 'vs last month',
    },
    {
      key: 'revenue',
      label: 'Monthly Revenue',
      icon: CurrencyDollar,
      value: formatCompact(revenue.value),
      demo: revenue.demo,
      trend: revenue.demo ? '+17.9%' : (latestRealChartRow && previousRealChartRow && Number(previousRealChartRow.revenue) ? `${(((Number(latestRealChartRow.revenue) - Number(previousRealChartRow.revenue)) / Math.abs(Number(previousRealChartRow.revenue))) * 100) >= 0 ? '+' : ''}${(((Number(latestRealChartRow.revenue) - Number(previousRealChartRow.revenue)) / Math.abs(Number(previousRealChartRow.revenue))) * 100).toFixed(1)}%` : 'Reported'),
      trendTone: revenue.demo || (latestRealChartRow && previousRealChartRow && Number(latestRealChartRow.revenue) >= Number(previousRealChartRow.revenue)) ? 'positive' : 'negative',
      footer: revenue.demo ? 'latest month' : 'vs previous month',
    },
    {
      key: 'netBurn',
      label: 'Net Burn',
      icon: Fire,
      value: formatCompact(netBurn.value),
      demo: netBurn.demo,
      trend: netBurn.demo ? 'Controlled' : Number(netBurn.value) <= 0 ? 'Cash positive' : 'Monitor',
      trendTone: netBurn.demo || Number(netBurn.value) <= 0 ? 'positive' : 'warning',
      footer: netBurn.demo ? 'burn − revenue' : 'burn minus revenue',
    },
  ];

  const visibleMetricDefinitions = metricDefinitions.filter((item) => selectedMetricKeys.includes(item.key));

  const enrichedChartData = useMemo(() => baseChartData.map((row, index, rows) => {
    const prev = rows[index - 1];
    const mrrValue = Number(row.mrr || 0);
    const revenueValue = Number(row.revenue || 0);
    const burnValue = Number(row.burn || 0);
    const customerValue = Number(row.customers || 0);
    const headcountValue = Math.max(1, Number(row.headcount || 0));
    const prevMrr = Number(prev?.mrr || 0);
    const prevRevenue = Number(prev?.revenue || 0);
    const prevCash = Number(prev?.cash || 0);
    const prevCustomers = Number(prev?.customers || 0);
    const cashValue = Number(row.cash || 0);
    return {
      ...row,
      netBurn: burnValue - revenueValue,
      revenuePerEmployee: revenueValue / headcountValue,
      mrrGrowth: prevMrr ? ((mrrValue - prevMrr) / Math.abs(prevMrr)) * 100 : 0,
      customerGrowth: prevCustomers ? ((customerValue - prevCustomers) / Math.abs(prevCustomers)) * 100 : 0,
      cashFlow: prev ? cashValue - prevCash : 0,
      arpu: customerValue ? revenueValue / customerValue : 0,
      revenueGrowth: prevRevenue ? ((revenueValue - prevRevenue) / Math.abs(prevRevenue)) * 100 : 0,
    };
  }), [baseChartData]);

  const chartData = useMemo(() => {
    if (duration === 'All') return enrichedChartData;
    const count = Number(duration.replace('M', '')) || 6;
    return enrichedChartData.slice(-count);
  }, [duration, enrichedChartData]);

  const visibleCharts = useMemo(
    () => CHARTS.filter((chart) => selectedGraphs.includes(chart.key)),
    [selectedGraphs]
  );

  const toggleGraph = (key) => {
    setSelectedGraphs((current) => {
      if (current.includes(key)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  };

  const graphCount = visibleCharts.length;
  const graphGridClass = graphCount === 1
    ? 'grid-cols-1'
    : graphCount === 2
      ? 'grid-cols-1 lg:grid-cols-2'
      : graphCount === 3
        ? 'grid-cols-1 md:grid-cols-3'
        : graphCount === 4
          ? 'grid-cols-1 md:grid-cols-2'
          : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

  // Scrolling layouts use fallback chart heights; desktop viewport-fit mode
  // lets the chart grid fill the measured available space.
  const chartHeight = graphCount === 1
    ? 390
    : graphCount === 2
      ? 320
      : graphCount === 3
        ? 285
        : graphCount === 4
          ? 175
          : 145;

  const firstName = user?.full_name?.split(' ')?.[0] || 'Founder';
  const greetingPrefix = `${greetingChoice.text}, `;
  const typedPrefix = typedGreeting.slice(0, Math.min(typedGreeting.length, greetingPrefix.length));
  const typedName = typedGreeting.length > greetingPrefix.length
    ? typedGreeting.slice(greetingPrefix.length)
    : '';
  const chartRange = chartData.length ? `${chartData[0]?.month || ''} – ${chartData[chartData.length - 1]?.month || ''}` : 'Recent months';

  return (
    <div ref={dashboardRoot} className="bg-background min-h-full fb-dashboard-motion fb-comfort-light">
      <style>{`
        @keyframes fbDashRise {
          from { opacity: 0; transform: translateY(18px) scale(.992); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fbDashFade {
          from { opacity: 0; transform: translateY(9px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fbDashPop {
          0% { opacity: 0; transform: translateY(12px) scale(.965); }
          70% { transform: translateY(-2px) scale(1.008); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fbGreetingCaret {
          0%, 45% { opacity: 1; }
          46%, 100% { opacity: 0; }
        }
        .fb-greeting-caret {
          display: inline-block;
          width: 2px;
          height: .82em;
          margin-left: 3px;
          border-radius: 999px;
          vertical-align: -.04em;
          background: currentColor;
          animation: fbGreetingCaret 780ms steps(1, end) infinite;
        }
        .fb-greeting-emoji {
          display: inline-block;
          margin-left: .28em;
          transform: translateY(-.04em) scale(1);
          transition: opacity 1800ms ease, transform 1800ms ease, filter 1800ms ease;
        }
        .fb-greeting-emoji.is-visible { opacity: 1; transform: translateY(-.04em) scale(1); filter: blur(0); }
        .fb-greeting-emoji.is-fading { opacity: 0; transform: translateY(-.08em) scale(.9); filter: blur(2px); }
        .fb-dashboard-motion .fb-animate-rise { animation: fbDashRise 620ms cubic-bezier(.16,1,.3,1) both; }
        .fb-dashboard-motion .fb-animate-fade { animation: fbDashFade 520ms cubic-bezier(.2,.8,.2,1) both; }
        .fb-dashboard-motion .fb-metric-card { animation: fbDashPop 560ms cubic-bezier(.16,1,.3,1) both; transition: transform 220ms cubic-bezier(.2,.8,.2,1), box-shadow 220ms ease, border-color 220ms ease; }
        .fb-dashboard-motion .fb-metric-card:nth-child(1) { animation-delay: 180ms; }
        .fb-dashboard-motion .fb-metric-card:nth-child(2) { animation-delay: 220ms; }
        .fb-dashboard-motion .fb-metric-card:nth-child(3) { animation-delay: 260ms; }
        .fb-dashboard-motion .fb-metric-card:nth-child(4) { animation-delay: 300ms; }
        .fb-dashboard-motion .fb-metric-card:nth-child(5) { animation-delay: 340ms; }
        .fb-dashboard-motion .fb-metric-card:hover { transform: translateY(-4px) scale(1.012); box-shadow: 0 14px 32px rgba(15,23,42,.10); }
        @keyframes fbChartReveal {
          0% { opacity: 0; transform: translateY(16px) scale(.965); filter: blur(3px); }
          55% { opacity: 1; transform: translateY(-2px) scale(1.008); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes fbChartSweep {
          0% { transform: translateX(-145%) skewX(-18deg); opacity: 0; }
          18% { opacity: .8; }
          65% { opacity: .22; }
          100% { transform: translateX(175%) skewX(-18deg); opacity: 0; }
        }
        .fb-dashboard-motion .fb-chart-card {
          animation: fbChartReveal 760ms cubic-bezier(.16,1,.3,1) both;
          transition: transform 260ms cubic-bezier(.2,.8,.2,1), box-shadow 260ms ease, border-color 260ms ease;
        }
        .fb-dashboard-motion .fb-chart-card::after {
          content: "";
          pointer-events: none;
          position: absolute;
          inset: 0;
          z-index: 2;
          width: 34%;
          background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--primary) 8%, transparent), transparent);
          animation: fbChartSweep 980ms cubic-bezier(.16,1,.3,1) both;
          animation-delay: inherit;
        }
        .fb-dashboard-motion .fb-chart-card:hover {
          transform: translateY(-5px) scale(1.008);
          box-shadow: 0 22px 50px rgba(15,23,42,.15);
          border-color: color-mix(in srgb, var(--primary) 34%, var(--border));
        }
        .fb-dashboard-motion .fb-graph-toggle { transition: transform 170ms ease, background-color 170ms ease, border-color 170ms ease, color 170ms ease, box-shadow 170ms ease; }
        .fb-dashboard-motion .fb-graph-toggle:hover:not(:disabled) { transform: translateY(-2px); }
        .fb-dashboard-motion .fb-graph-toggle:active:not(:disabled) { transform: translateY(0) scale(.97); }
        .fb-dashboard-motion .fb-chart-card .recharts-layer,
        .fb-dashboard-motion .fb-chart-card .recharts-surface {
          transform-origin: center;
        }
        .fb-dashboard-motion .fb-chart-card .recharts-curve,
        .fb-dashboard-motion .fb-chart-card .recharts-rectangle {
          transition: filter 220ms ease, opacity 220ms ease;
        }
        .fb-dashboard-motion .fb-chart-card:hover .recharts-curve,
        .fb-dashboard-motion .fb-chart-card:hover .recharts-rectangle {
          filter: drop-shadow(0 2px 3px color-mix(in srgb, var(--primary) 18%, transparent));
        }
        @media (prefers-reduced-motion: reduce) {
          .fb-dashboard-motion *, .fb-dashboard-motion *::before, .fb-dashboard-motion *::after { animation-duration: 1ms !important; animation-delay: 0ms !important; transition-duration: 1ms !important; }
        }
      `}</style>
      <div className="fb-dashboard-content max-w-[1280px] mx-auto px-3 sm:px-4 md:px-5 py-4 md:py-5">
        <header className="fb-animate-rise mb-5 pb-2 pt-1 md:mb-6">
          <div className="fb-dashboard-greeting-copy">
          <div className="mb-2 flex items-center gap-2">
            <span className="h-2 w-2 rounded-sm bg-[#0D9488]" />
            <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#0D9488] dark:text-teal-300 sm:text-[10px]">Startup dashboard</span>
          </div>
          <div>
            <h1 className="font-heading text-[30px] font-bold leading-[1.03] tracking-[-0.045em] text-text-primary sm:text-[35px] md:text-[40px]" aria-label={`${greetingChoice.text}, ${firstName}.`}>
              <span aria-hidden="true">
                {typedPrefix}
                <span className="text-[#0D9488] dark:text-teal-300">{typedName}</span>
                {greetingTyping && <span className="fb-greeting-caret" />}
                {!greetingTyping && (
                  <span className={`fb-greeting-emoji ${greetingEmojiVisible ? 'is-visible' : 'is-fading'}`}>
                    {greetingChoice.emoji}
                  </span>
                )}
              </span>
            </h1>
            <p className="mt-2 max-w-[680px] text-[11px] text-text-muted sm:text-[12px]">
              Your operating snapshot, current priorities, and the signals worth watching.
            </p>
          </div>
          </div>
        </header>

        <section className="fb-dashboard-snapshot fb-animate-fade mb-5 [animation-delay:150ms]">
          <div className="fb-snapshot-heading mb-2.5 flex items-start justify-between gap-3">
            <div>
              <h2 id="dashboard-snapshot-heading" className="text-[14px] font-extrabold uppercase tracking-[0.08em] text-[#0D9488] dark:text-teal-300 sm:text-[15px]">{activityOpen ? 'Activity Pulse' : 'Key metrics'}</h2>
              <div className="fb-snapshot-subtitle mt-0.5 text-[11px] text-text-secondary">
                {activityOpen ? 'Your latest updates and priorities' : `Latest operating snapshot · choose ${MIN_METRICS}–${MAX_METRICS} metrics`}
              </div>
            </div>

            <div className="fb-snapshot-actions">
              <button type="button" className="fb-activity-toggle" onClick={() => setActivityOpen((open) => !open)} aria-expanded={activityOpen} aria-controls="startup-activity-panel" aria-label={`${activityOpen ? 'Hide Activity Pulse and show Key Metrics' : 'Show Activity Pulse'}, ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`} title={activityOpen ? 'Back to Key Metrics' : 'Show Activity Pulse'} data-testid="startup-activity-toggle">
                <Bell size={18} weight="bold" />
                <span className="fb-unread-badge" data-empty={unreadCount === 0} aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
              </button>
            <div className="fb-metric-customize-slot" data-hidden={activityOpen} aria-hidden={activityOpen} inert={activityOpen}>
            <button
              type="button"
              onClick={() => setMetricPickerOpen((open) => !open)}
              className="fb-metric-customize"
              aria-expanded={metricPickerOpen}
              aria-controls="dashboard-metric-picker"
              aria-label="Customize key metrics"
              title={`Customize key metrics (${selectedMetricKeys.length}/${MAX_METRICS} selected)`}
              data-testid="dashboard-metric-customize"
            >
              <SlidersHorizontal size={12} weight="bold" />
              Customize
              <span className="fb-customize-count">{selectedMetricKeys.length}/{MAX_METRICS}</span>
            </button>
            </div>
            </div>
          </div>

          <div className="fb-snapshot-slot">
          <div className={`fb-snapshot-panel ${activityOpen ? '' : 'is-active'}`} aria-hidden={activityOpen} inert={activityOpen}>
          <div className="fb-snapshot-content">
          {metricPickerOpen && (
            <div id="dashboard-metric-picker" className="mb-2.5 rounded-lg border border-border bg-surface p-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[9px] font-bold text-text-primary">Choose dashboard metrics</div>
                  <div className="mt-0.5 text-[8px] text-text-muted">
                    Minimum {MIN_METRICS}, maximum {MAX_METRICS}. Your selection is saved on this browser.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMetricKeys(DEFAULT_METRIC_KEYS)}
                  className="text-[9px] font-bold text-[#0D9488] hover:underline"
                >
                  Reset
                </button>
              </div>

              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 xl:grid-cols-8">
                {metricDefinitions.map((item) => {
                  const selected = selectedMetricKeys.includes(item.key);
                  const cannotRemove = selected && selectedMetricKeys.length <= MIN_METRICS;
                  const cannotAdd = !selected && selectedMetricKeys.length >= MAX_METRICS;
                  const disabled = cannotRemove || cannotAdd;
                  const Icon = item.icon;

                  return (
                    <button
                      type="button"
                      key={item.key}
                      onClick={() => toggleMetric(item.key)}
                      disabled={disabled}
                      aria-pressed={selected}
                      title={cannotRemove ? `Keep at least ${MIN_METRICS} metrics` : cannotAdd ? `You can show up to ${MAX_METRICS} metrics` : `${selected ? 'Hide' : 'Show'} ${item.label}`}
                      className={`flex min-h-[48px] items-center gap-2 rounded-md border px-2 py-2 text-left transition ${
                        selected
                          ? 'border-[#0D9488]/35 bg-[#0D9488]/[0.07] text-text-primary'
                          : 'border-border bg-background/40 text-text-muted hover:border-[#0D9488]/25 hover:text-text-primary'
                      } ${disabled ? 'cursor-not-allowed opacity-55' : ''}`}
                    >
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${selected ? 'bg-[#0D9488]/12 text-[#0D9488]' : 'bg-secondary text-text-muted'}`}>
                        <Icon size={12} weight="bold" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[9px] font-bold">{item.label}</span>
                      {selected && <Check size={10} weight="bold" className="shrink-0 text-[#0D9488]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ '--metric-count': visibleMetricDefinitions.length }} className={`fb-key-metrics-grid grid gap-2.5 ${
            visibleMetricDefinitions.length === 3
              ? 'grid-cols-1 sm:grid-cols-3'
              : visibleMetricDefinitions.length === 4
                ? 'grid-cols-2 xl:grid-cols-4'
                : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5'
          }`}>
            {visibleMetricDefinitions.map((item) => (
              <MetricCard
                key={item.key}
                label={item.label}
                icon={item.icon}
                value={item.value}
                demo={item.demo}
                onDemoClick={addMonthlyData}
                trend={item.trend}
                trendTone={item.trendTone}
                footer={item.footer}
              />
            ))}
          </div>
          </div>
          </div>
          <div id="startup-activity-panel" className={`fb-snapshot-panel ${activityOpen ? 'is-active' : ''}`} aria-hidden={!activityOpen} inert={!activityOpen}>
            <div className="fb-snapshot-content">
              <ActivityPulse compact subtle active={activityOpen} onUnreadCountChange={setUnreadCount} />
            </div>
          </div>
          </div>
        </section>

        <div className="fb-dashboard-lower-grid grid min-w-0 gap-3.5 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-stretch">
          <section className="fb-performance-panel fb-animate-rise flex min-w-0 flex-col overflow-visible rounded-lg border border-border bg-surface p-3.5 sm:p-4 xl:h-[660px] xl:overflow-hidden [animation-delay:210ms]">
          <div className="mb-3 flex shrink-0 flex-col gap-3">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-[14px] font-extrabold uppercase tracking-[0.08em] text-[#0D9488] dark:text-teal-300 sm:text-[15px]">Performance & trends</h2>
                  {useDemoCharts && <DemoTag prominent />}
                </div>
                <p className="text-[10px] sm:text-[11px] text-text-muted mt-1">
                  Toggle the core metrics you want to compare
                </p>
                {useDemoCharts && (
                  <p className="text-[10px] sm:text-[11px] text-text-muted mt-1">
                    Illustrative trends are shown because there are not enough monthly updates yet.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
                <div className="flex items-center gap-1 rounded-md border border-border bg-background p-1" aria-label="Graph duration">
                  <span className="hidden sm:inline px-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-text-muted">Range</span>
                  {['3M', '6M', '12M', 'All'].map((range) => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setDuration(range)}
                      className={`fb-graph-toggle rounded px-2.5 py-1.5 text-[10px] font-bold transition-all ${duration === range ? 'bg-[#0D9488] text-white border border-[#0D9488]' : 'text-text-muted hover:text-text-primary border border-transparent hover:bg-secondary'}`}
                      data-testid={`graph-duration-${range.toLowerCase()}`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
                <div className="hidden md:block rounded-lg border border-border px-2.5 py-1.5 text-[9px] font-semibold text-text-muted">{chartRange}</div>
              </div>
            </div>

            <div className="pt-1">
              <div className="mb-2 flex items-center justify-between gap-3 px-0.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-muted">Graphs</span>
                <span className="text-[10px] text-text-muted">{selectedGraphs.length} of {CHARTS.length} visible</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-1.5">
                {CHARTS.map((chart) => {
                  const active = selectedGraphs.includes(chart.key);
                  return (
                    <button
                      type="button"
                      key={chart.key}
                      onClick={() => toggleGraph(chart.key)}
                      disabled={active && selectedGraphs.length === 1}
                      aria-pressed={active}
                      className="min-h-[34px] rounded-md border px-2.5 py-2 text-[10px] font-semibold text-left transition-all disabled:cursor-default"
                      style={active ? {
                        backgroundColor: 'color-mix(in srgb, #0D9488 10%, var(--card))',
                        borderColor: 'color-mix(in srgb, #0D9488 42%, var(--border))',
                        color: 'var(--foreground)',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                      } : {
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        color: 'var(--muted-foreground)',
                      }}
                      onMouseEnter={(event) => {
                        if (!active) {
                          event.currentTarget.style.backgroundColor = 'var(--secondary)';
                          event.currentTarget.style.color = 'var(--foreground)';
                        }
                      }}
                      onMouseLeave={(event) => {
                        if (!active) {
                          event.currentTarget.style.backgroundColor = 'var(--card)';
                          event.currentTarget.style.color = 'var(--muted-foreground)';
                        }
                      }}
                      data-testid={`graph-toggle-${chart.key}`}
                      title={active && selectedGraphs.length === 1 ? 'At least one graph must remain visible' : `${active ? 'Hide' : 'Show'} ${chart.title}`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="h-1.5 w-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: active ? '#0D9488' : 'var(--muted)' }}
                        />
                        <span className="truncate">{chart.title}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div data-dense={graphCount >= 4} style={{ '--graph-columns': graphCount === 1 ? 1 : graphCount <= 4 ? 2 : 3 }} className={`fb-chart-grid grid min-h-0 flex-1 auto-rows-auto gap-3 overflow-visible xl:auto-rows-fr xl:overflow-hidden ${graphGridClass}`}>
            {visibleCharts.map((chart, index) => (
              <DashboardChart
                key={chart.key}
                chart={chart}
                data={chartData}
                demo={useDemoCharts}
                formatCompact={formatCompact}
                chartHeight={chartHeight}
                compact={graphCount >= 4}
                animationDelay={260 + (index * 55)}
                onDemoClick={addMonthlyData}
              />
            ))}
          </div>
          </section>

          {/* Tabbed programs and services carousel with Vault quick access. */}
          <FounderDashboardSideRail user={user} />
        </div>



      </div>
    </div>
  );
};

export default StartupDashboardNew;
